import { get, post_with_config } from '@/services/http';

/**
 * Interface representing the status of a supplier import job.
 * Adjust fields based on the actual backend API response.
 */
export interface ProcessedSupplierSummary {
  name: string;
  status: 'CREATED' | 'UPDATED' | 'FAILED';
  productsLinked: number;
  warnings: string[];
  errors: string[];
}

export interface ImportJobStatus {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PARTIAL_SUCCESS';
  progress: number; // e.g., 0-100
  phase?: string; // e.g., "VALIDATING_FILE", "PARSING_CSV", "PROCESSING_ROWS", "COMPLETED"
  totalRecords?: number;
  processedRecords?: number;
  successfulRecords?: number;
  failedRecords?: number;
  supplierCount?: number;
  errors?: Array<{ row?: number; message: string; details?: any }>;
  warnings?: Array<{ row?: number; message: string; details?: any }>;
  resultsUrl?: string; // Link to download results/errors CSV
  createdAt: string;
  updatedAt: string;
  message?: string; // General message about the job status
  results?: {
    totalRecords: number;
    successfulRecords: number;
    failedRecords: number;
    supplierCount: number;
    processedSummary: ProcessedSupplierSummary[];
  };
}

/**
 * Model for handling supplier CSV import related API calls.
 */
export class SupplierImportModel {

  /**
   * Fetches the CSV template string for supplier imports.
   * The backend is expected to return raw text/csv content.
   * @returns A promise that resolves to the CSV template string.
   */
  public static async getImportTemplate(): Promise<string> {
    try {
      // Expecting a JSON response like { csvData: "csv_string_content" }
      const response = await get<{ csvData: string }>('/suppliers/import/template', {
        headers: { Accept: 'application/json' }, // Standard JSON request
        // responseType: 'text' is REMOVED as we expect JSON now
      });

      if (response && typeof response.csvData === 'string') {
        return response.csvData;
      } else {
        console.warn('GetImportTemplate: Unexpected response format. Expected { csvData: string }.');
        // Attempt to handle if the raw response IS the string (fallback, less likely now)
        if (typeof response === 'string') { return response as string; }
        return ''; // Or throw an error if csvData is not found
      }
    } catch (error) {
      console.error('Error fetching supplier import template:', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }

  /**
   * Fetches all suppliers for the current merchant as a CSV string.
   * Columns match the import template so the file can be edited and re-imported.
   */
  public static async exportAllSuppliers(): Promise<string> {
    try {
      const response = await get<{ csvData: string }>('/suppliers/export', {
        headers: { Accept: 'application/json' },
      });
      if (response && typeof response.csvData === 'string') {
        return response.csvData;
      }
      if (typeof response === 'string') { return response as string; }
      return '';
    } catch (error) {
      console.error('Error exporting suppliers:', error);
      throw error;
    }
  }

  public static async getUnlinkedSkusTemplate(): Promise<string> {
    try {
      // Expecting a JSON response like { csvData: "csv_string_content" }
      const response = await get<{ csvData: string }>('/suppliers/import/unlinked-skus-template', {
        headers: { Accept: 'application/json' }, // Standard JSON request
        // responseType: 'text' is REMOVED as we expect JSON now
      });

      if (response && typeof response.csvData === 'string') {
        return response.csvData;
      } else {
        console.warn('GetUnlinkedSkusTemplate: Unexpected response format. Expected { csvData: string }.');
        // Attempt to handle if the raw response IS the string (fallback, less likely now)
        if (typeof response === 'string') { return response as string; }
        return ''; // Or throw an error if csvData is not found
      }
    } catch (error) {
      console.error('Error fetching supplier import template:', error);
      throw error; // Re-throw the error to be handled by the caller
      }
  }

  /**
   * Uploads a supplier CSV file to initiate an import job.
   * @param file The CSV file object to upload.
   * @returns A promise that resolves to an object containing the jobId and a message.
   */
  public static async uploadSupplierCSV(file: File): Promise<{ jobId: string; message: string }> {
    try {
      const formData = new FormData();
      formData.append('supplierCsvFile', file, file.name);

      // Ensure 'postMultipart' is correctly implemented in your http service
      // to handle FormData and return the expected JSON response.
      const response = await post_with_config<{ jobId: string; message: string }>('/suppliers/import/upload', formData);
      return response; // Assuming post returns the body directly
    } catch (error) {
      console.error('Error uploading supplier CSV:', error);
      throw error;
    }
  }

  /**
   * Fetches the status of a specific import job.
   * @param jobId The ID of the import job.
   * @returns A promise that resolves to the ImportJobStatus object.
   */
  public static async getImportJobStatus(jobId: string): Promise<ImportJobStatus> {
    try {
      // The API now returns a response like { message: '...', data: { ...job_status } }
      const response = await get<{ data: ImportJobStatus }>(`/suppliers/import/status/${jobId}`);
      // Return the nested data object which is the actual job status
      return response.data;
    } catch (error) {
      console.error(`Error fetching status for job ${jobId}:`, error);
      throw error;
    }
  }

  /**
   * Triggers the processing of a previously uploaded supplier CSV file.
   * @param jobId The ID of the job to process.
   * @returns A promise that resolves to an object containing the jobId and a message.
   */
  public static async processSupplierImport(jobId: string): Promise<{ jobId: string; message: string }> {
    try {
      // Backend route is POST /api/suppliers/import/process/:jobId
      // Send an empty object as payload if no body is required.
      const response = await post_with_config<{ jobId: string; message: string }>(`/suppliers/import/process/${jobId}`, {});
      return response; // Assuming post returns the body directly
    } catch (error) {
      console.error(`Error triggering processing for job ${jobId}:`, error);
      throw error;
    }
  }

  /**
   * Fetches the history of supplier import jobs.
   * (Optional - if you implement GET /api/suppliers/import/history)
   * @param params Parameters for pagination and filtering.
   * @returns A promise that resolves to a list of job statuses and pagination info.
   */
  // static async getImportHistory(params: { page?: number; limit?: number; status?: string } = {}): Promise<{ jobs: ImportJobStatus[]; total: number; page: number; limit: number }> {
  //   try {
  //     const response = await get<{ jobs: ImportJobStatus[]; total: number; page: number; limit: number }>('/suppliers/import/history', { params });
  //     return response;
  //   } catch (error) {
  //     console.error('Error fetching supplier import history:', error);
  //     throw error;
  //   }
  // }
}
